import { useState, useEffect, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { CJSubscriptionsValidationSchema } from 'validators/schemas.validation';
import { useTranslation } from 'react-i18next';
import { noop } from 'lodash';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useHasPermission from 'hooks/UseHasPermission';
import { useMenuItem } from 'hooks/UseMenuItem';
// Import Services
import { UtilsHttpService } from 'services/http';
import { getCustomerJourneyFormFieldTranslatableErrorText } from 'services/customerJourney/helpers';
// Import UI Components
import { Dropdown, SkeletonLoader } from '@geneui/components';
// Import Constants
import { l, PARTNER_SETTINGS } from 'constants/common';
// Import Enums
import { MenuItemIdEnum } from 'enums';

const CJSubscriptions = ({ defaultValue, getUpdate, option }) => {
    const { t } = useTranslation();
    const { doGetRequest } = useRequest();
    const { hasPartnerSettings } = useHasPermission();

    const isAllowTemplateCategory = hasPartnerSettings(PARTNER_SETTINGS.IsAllowTemplateCategory);

    const { convertedData: templateCategories, isLoading: isTemplateCategoryLoading } = useMenuItem(
        isAllowTemplateCategory ? MenuItemIdEnum.TemplateCategory : null,
    );

    const templateCategoriesList = useMemo(() => {
        return templateCategories.map(({ label, value }) => ({
            value: +value,
            label,
        }));
    }, [templateCategories]);

    const [channelsList, setChannelsList] = useState([]);
    const [isChannelsListLoading, setIsLoadingChannelsList] = useState(true);

    const formik = useFormik({
        initialValues: {
            channel: defaultValue?.channel,
            category: defaultValue?.category,
            allowCategory: isAllowTemplateCategory,
        },
        onSubmit: noop,
        validationSchema: CJSubscriptionsValidationSchema(option),
    });

    const { values, setFieldValue, errors, submitForm } = formik;
    const { channel, category } = values;

    const { getChannelsListRequest } = useCancelableRequests({
        getChannelsListRequest: UtilsHttpService.getChannelsList,
    });

    useEffect(() => {
        getUpdate({
            channel,
            category,
            allowCategory: isAllowTemplateCategory,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channel, category, isAllowTemplateCategory]);

    const init = () => {
        doGetRequest(getChannelsListRequest.request, {
            successCallback: (data) => {
                setChannelsList(
                    data.map(({ Id, Name }) => {
                        return {
                            value: +Id,
                            label: Name,
                        };
                    }),
                );
                setIsLoadingChannelsList(false);
            },
        });
        submitForm();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    return (
        <div className="cj-subscriptions-block">
            <SkeletonLoader isBusy={isChannelsListLoading} width={'23.6rem'} height={'3.6rem'}>
                <Dropdown
                    defaultValue={channel}
                    inputSize="default"
                    placeholder={l.Channels}
                    appearance="outline"
                    data={channelsList}
                    isLoading={isChannelsListLoading}
                    onChange={(event) => setFieldValue('channel', event.value)}
                    isValid={!errors?.channel}
                    errorText={getCustomerJourneyFormFieldTranslatableErrorText(t, errors?.channel)}
                />
            </SkeletonLoader>
            {isAllowTemplateCategory && (
                <SkeletonLoader isBusy={isTemplateCategoryLoading} width={'23.6rem'} height={'3.6rem'}>
                    <Dropdown
                        defaultValue={category}
                        inputSize="default"
                        placeholder={l.Category}
                        appearance="outline"
                        data={templateCategoriesList}
                        isLoading={isTemplateCategoryLoading}
                        onChange={(event) => setFieldValue('category', event.value)}
                        isValid={!errors?.category}
                        errorText={getCustomerJourneyFormFieldTranslatableErrorText(t, errors?.category)}
                    />
                </SkeletonLoader>
            )}
        </div>
    );
};

CJSubscriptions.propTypes = {
    defaultValue: PropTypes.object,
    getUpdate: PropTypes.func.isRequired,
    option: PropTypes.object.isRequired,
};

export default memo(CJSubscriptions);
